import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';

interface SupplyReq {
  id: number;
  requestedByName: string;
  items: string;
  notes: string | null;
  hasPhoto: boolean;
  status: 'pending' | 'fulfilled';
  requestedAt: string;
  fulfilledBy: string | null;
  fulfilledAt: string | null;
}

function fmt(ts: string) {
  return new Date(ts).toLocaleString('en-AU', {
    timeZone: 'Australia/Brisbane',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Inline photo viewer. The photo endpoint needs the auth token, so the image
 *  is fetched as a blob and shown here rather than in a new tab: the old
 *  window.open path ran after the fetch (outside the click), which popup
 *  blockers silently drop, so "View photo" appeared to do nothing. */
function PhotoLightbox({ id, onClose }: { id: number; onClose: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  // Latest onClose without making it an effect dependency: the parent passes
  // a fresh closure each render (and re-renders on its 60s refetch), which
  // would otherwise re-fetch the photo and flicker.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    api.supplyRequestPhotoUrl(id)
      .then((u) => { if (cancelled) URL.revokeObjectURL(u); else { objectUrl = u; setUrl(u); } })
      .catch(() => { if (!cancelled) setError(true); });
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCloseRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => { cancelled = true; window.removeEventListener('keydown', onKey); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [id]);
  return (
    <div role="dialog" aria-modal="true" aria-label="Supply request photo" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <button onClick={onClose} aria-label="Close" style={{ position: 'absolute', top: 12, right: 12, width: 40, height: 40, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.9)', fontSize: 22, cursor: 'pointer' }}>✕</button>
      {error ? (
        <div style={{ color: '#fff', fontSize: 16 }}>Couldn’t load the photo.</div>
      ) : url ? (
        <img src={url} alt="Supply request photo" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '96vw', maxHeight: '92vh', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }} />
      ) : (
        <div style={{ color: '#fff', fontSize: 16 }}>Loading photo…</div>
      )}
    </div>
  );
}

export function SupplyRequests() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['supply-requests'],
    queryFn: () => api.getSupplyRequests(),
    refetchInterval: 60_000,
  });
  const fulfil = useMutation({
    mutationFn: (id: number) => api.fulfillSupplyRequest(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['supply-requests'] }),
  });

  const [photoId, setPhotoId] = useState<number | null>(null);

  const requests: SupplyReq[] = data?.requests || [];
  const pending = requests.filter((r) => r.status === 'pending');

  const card = (r: SupplyReq): React.CSSProperties => ({
    border: '1px solid var(--color-border, #e0e0e0)',
    borderLeft: `4px solid ${r.status === 'pending' ? 'var(--color-gold, #FEC553)' : 'var(--color-teal, #00B097)'}`,
    borderRadius: 8,
    padding: 'var(--spacing-3, 16px)',
    marginBottom: 12,
    background: 'var(--color-surface, #fff)',
  });

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: 'var(--spacing-3, 16px)' }}>
      <h2 style={{ marginTop: 0 }}>
        Supply requests{' '}
        {pending.length > 0 && (
          <span style={{ background: 'var(--color-error, #EE2937)', color: '#fff', borderRadius: 999, padding: '2px 10px', fontSize: 14, verticalAlign: 'middle' }}>
            {pending.length} pending
          </span>
        )}
      </h2>

      {isLoading && <p>Loading…</p>}
      {!isLoading && requests.length === 0 && (
        <p style={{ color: 'var(--color-text-muted, #666)' }}>No supply requests.</p>
      )}

      {requests.map((r) => (
        <div key={r.id} style={card(r)}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{r.requestedByName}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-muted, #666)' }}>
                requested {fmt(r.requestedAt)}
              </div>
              <div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>{r.items}</div>
              {r.notes && (
                <div style={{ marginTop: 6, fontSize: 13, color: 'var(--color-text-muted, #666)' }}>
                  Note: {r.notes}
                </div>
              )}
              {r.hasPhoto && (
                <button
                  onClick={() => setPhotoId(r.id)}
                  style={{ marginTop: 8, background: 'none', border: '1px solid var(--color-border,#ccc)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
                >
                  📷 View photo
                </button>
              )}
            </div>
            <div style={{ textAlign: 'right', minWidth: 120 }}>
              {r.status === 'pending' ? (
                <button
                  onClick={() => fulfil.mutate(r.id)}
                  disabled={fulfil.isPending}
                  style={{ background: 'var(--color-teal, #00B097)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Mark fulfilled
                </button>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--color-teal, #00B097)' }}>
                  ✓ fulfilled
                  {r.fulfilledAt && <div style={{ color: 'var(--color-text-muted,#666)' }}>{fmt(r.fulfilledAt)}</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
      {photoId != null && <PhotoLightbox id={photoId} onClose={() => setPhotoId(null)} />}
    </div>
  );
}
